// communicationStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
    communicationService,
    EmailTemplate,
    EmailLog,
    CommunicationStats,
    CreateEmailTemplateData,
    CreateEmailLogData,
} from '../services/communication.service';

// Built-in Invoice Email Template (system template - cannot be edited/deleted)
export const BUILT_IN_INVOICE_TEMPLATE: EmailTemplate = {
    id: 'builtin-invoice-template',
    name: 'Invoice Email',
    category: 'invoice',
    subject: 'Invoice {{invoice.number}} from {{business.name}}',
    bodyHtml: `Dear {{client.name}},

Please find attached invoice {{invoice.number}} for your reference.

Invoice Summary:
• Invoice Number: {{invoice.number}}
• Amount Due: {{invoice.amount}}
• Due Date: {{invoice.dueDate}}
• Payment Terms: {{invoice.paymentTerms}}

If you have any questions regarding this invoice, please don't hesitate to contact us.

Thank you for your business!

Best regards,
{{business.name}}`,
    variables: ['invoice.number', 'invoice.amount', 'invoice.dueDate', 'invoice.paymentTerms', 'client.name', 'business.name'],
    teamId: 'system',
    createdBy: 'system',
    isActive: true,
    isBuiltIn: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
};

// Built-in Contract Signature Request Template (system template - cannot be edited/deleted)
export const BUILT_IN_SIGNATURE_TEMPLATE: EmailTemplate = {
    id: 'builtin-signature-template',
    name: 'Contract Signature Request',
    category: 'contract',
    subject: 'Contract for Signature: {{contract.title}}',
    bodyHtml: `Hi {{contact.firstName}},

I hope this message finds you well.

Please find the contract "{{contract.title}}" ready for your review and signature. You can access and sign the contract using the secure link below:

🔗 Signature Link: {{signature.link}}

This link will expire on {{signature.expiryDate}}.

If you have any questions or need any clarifications before signing, please don't hesitate to reach out.

Best regards`,
    variables: ['contact.firstName', 'contract.title', 'signature.link', 'signature.expiryDate'],
    teamId: 'system',
    createdBy: 'system',
    isActive: true,
    isBuiltIn: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
};

interface CommunicationState {
    // Data
    templates: EmailTemplate[];
    emails: EmailLog[];
    stats: CommunicationStats | null;

    // UI State
    isLoading: boolean;
    error: string | null;
    searchTerm: string;
    filterStatus: string;
    filterCategory: string;
    viewMode: 'grid' | 'list';

    // Actions
    setSearchTerm: (term: string) => void;
    setFilterStatus: (status: string) => void;
    setFilterCategory: (category: string) => void;
    setViewMode: (mode: 'grid' | 'list') => void;
    clearError: () => void;

    // Template Actions
    fetchTemplates: (category?: string) => Promise<void>;
    createTemplate: (data: CreateEmailTemplateData) => Promise<EmailTemplate>;
    updateTemplate: (id: string, data: Partial<CreateEmailTemplateData>) => Promise<EmailTemplate>;
    deleteTemplate: (id: string) => Promise<void>;

    // Email Actions
    fetchEmails: () => Promise<void>;
    sendEmail: (data: CreateEmailLogData) => Promise<EmailLog>;
    updateEmailStatus: (id: string, status: EmailLog['status']) => Promise<EmailLog>;

    // Stats Actions
    fetchStats: () => Promise<void>;

    // Computed Getters
    getFilteredEmails: () => EmailLog[];
    getRecentEmails: (count?: number) => EmailLog[];
    getEmailsByStatus: (status: string) => EmailLog[];
}

// Built-in templates to always include
const BUILT_IN_TEMPLATES = [BUILT_IN_INVOICE_TEMPLATE, BUILT_IN_SIGNATURE_TEMPLATE];

export const useCommunicationStore = create<CommunicationState>()(
    devtools(
        persist(
            (set, get) => ({
                // Initial state
                templates: BUILT_IN_TEMPLATES,
                emails: [],
                stats: null,
                isLoading: false,
                error: null,
                searchTerm: '',
                filterStatus: 'all',
                filterCategory: 'all',
                viewMode: 'list',

                // Filter actions
                setSearchTerm: (term) => set({ searchTerm: term }),
                setFilterStatus: (status) => set({ filterStatus: status }),
                setFilterCategory: (category) => set({ filterCategory: category }),
                setViewMode: (mode) => set({ viewMode: mode }),
                clearError: () => set({ error: null }),

                // Fetch templates
                fetchTemplates: async (category) => {
                    try {
                        set({ isLoading: true, error: null });
                        const templates = await communicationService.getTemplates(category);
                        // Prepend built-in templates to fetched templates
                        set({ templates: [...BUILT_IN_TEMPLATES, ...templates], isLoading: false });
                    } catch (error) {
                        set({ error: (error as Error).message, isLoading: false });
                    }
                },

                // Create template
                createTemplate: async (data) => {
                    try {
                        set({ isLoading: true, error: null });
                        const template = await communicationService.createTemplate(data);
                        set((state) => ({
                            templates: [template, ...state.templates],
                            isLoading: false,
                        }));
                        return template;
                    } catch (error) {
                        set({ error: (error as Error).message, isLoading: false });
                        throw error;
                    }
                },

                // Update template
                updateTemplate: async (id, data) => {
                    try {
                        set({ isLoading: true, error: null });
                        const template = await communicationService.updateTemplate(id, data);
                        set((state) => ({
                            templates: state.templates.map((t) => (t.id === id ? template : t)),
                            isLoading: false,
                        }));
                        return template;
                    } catch (error) {
                        set({ error: (error as Error).message, isLoading: false });
                        throw error;
                    }
                },

                // Delete template
                deleteTemplate: async (id) => {
                    try {
                        set({ isLoading: true, error: null });
                        await communicationService.deleteTemplate(id);
                        set((state) => ({
                            templates: state.templates.filter((t) => t.id !== id),
                            isLoading: false,
                        }));
                    } catch (error) {
                        set({ error: (error as Error).message, isLoading: false });
                        throw error;
                    }
                },

                // Fetch emails
                fetchEmails: async () => {
                    try {
                        set({ isLoading: true, error: null });
                        const { searchTerm, filterStatus } = get();
                        const filters: { searchQuery?: string; status?: string } = {};
                        if (searchTerm) filters.searchQuery = searchTerm;
                        if (filterStatus !== 'all') filters.status = filterStatus;
                        const emails = await communicationService.getEmails(filters);
                        set({ emails, isLoading: false });
                    } catch (error) {
                        set({ error: (error as Error).message, isLoading: false });
                    }
                },

                // Send email
                sendEmail: async (data) => {
                    try {
                        set({ isLoading: true, error: null });
                        const email = await communicationService.sendEmail(data);
                        set((state) => ({
                            emails: [email, ...state.emails],
                            isLoading: false,
                        }));
                        // Refresh stats after sending
                        get().fetchStats();
                        return email;
                    } catch (error) {
                        set({ error: (error as Error).message, isLoading: false });
                        throw error;
                    }
                },

                // Update email status
                updateEmailStatus: async (id, status) => {
                    try {
                        set({ isLoading: true, error: null });
                        const email = await communicationService.updateEmailStatus(id, status);
                        set((state) => ({
                            emails: state.emails.map((e) => (e.id === id ? email : e)),
                            isLoading: false,
                        }));
                        // Refresh stats after status change
                        get().fetchStats();
                        return email;
                    } catch (error) {
                        set({ error: (error as Error).message, isLoading: false });
                        throw error;
                    }
                },

                // Fetch stats
                fetchStats: async () => {
                    try {
                        const stats = await communicationService.getStats();
                        set({ stats });
                    } catch (error) {
                        console.error('Failed to fetch stats:', error);
                    }
                },

                // Computed getters
                getFilteredEmails: () => {
                    const { emails, searchTerm, filterStatus } = get();
                    let filtered = [...emails];

                    if (searchTerm) {
                        const term = searchTerm.toLowerCase();
                        filtered = filtered.filter(
                            (e) =>
                                e.subject.toLowerCase().includes(term) ||
                                e.to.some((addr) => addr.toLowerCase().includes(term)) ||
                                e.partnership?.brand?.toLowerCase().includes(term)
                        );
                    }

                    if (filterStatus !== 'all') {
                        filtered = filtered.filter((e) => e.status === filterStatus);
                    }

                    return filtered;
                },

                getRecentEmails: (count = 10) => {
                    const { emails } = get();
                    return emails.slice(0, count);
                },

                getEmailsByStatus: (status) => {
                    const { emails } = get();
                    return emails.filter((e) => e.status === status);
                },
            }),
            {
                name: 'communication-storage',
                partialize: (state) => ({
                    viewMode: state.viewMode,
                    filterStatus: state.filterStatus,
                    filterCategory: state.filterCategory,
                }),
            }
        ),
        {
            name: 'communication-store',
        }
    )
);
